import { useLayoutEffect, useRef, useState } from "react";


interface SvgWrappableTextProps {
  text: string;
  color: string;
  x: number;
  top: number;
  fontFamily?: string;
  fontSize: number;
  lineHeight?: number;
  fontWeight: string;
  letterSpacing?: number;
  textAnchor: string;
  width: number;
  onLayout: (height: number, boxes: SVGRect[]) => void;
}

export default function SvgWrappableText(props: SvgWrappableTextProps) {
  const { text, color, x, top, fontFamily = 'Montserrat', fontSize, lineHeight = 1.3, fontWeight, textAnchor, width, onLayout, letterSpacing = 0 } = props;
  const headerTextRef = useRef<SVGTextElement>(null);
  const wrappedTextRef = useRef<SVGTextElement>(null);
  const [textLines, setTextLines] = useState<string[]>([]);
  const tspanRefs = useRef<Map<number, SVGTSpanElement | null>>(new Map());
  const [baslineOffset, setBaselineOffset] = useState(0);

  const headerY = top + baslineOffset;

  useLayoutEffect(() => {
    if (!headerTextRef || !headerTextRef.current || text.length === 0) {
      return;
    }
    const bbox = headerTextRef.current.getBBox();
    const currentBaseline = headerTextRef.current.y.baseVal.getItem(0).value;
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    let startIndex = 0;
  
    words.forEach((word, index) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = headerTextRef.current.getSubStringLength(startIndex, testLine.length)
  
      if (testWidth > width && index > 0) {
        const breakIndex = word.indexOf('​');
        if (breakIndex > 0) {
          const preBreak = word.substring(0, breakIndex);
          const postBreak = word.substring(breakIndex + 1);
          const testLineBreakWord = currentLine ? `${currentLine} ${preBreak}-` : (preBreak + '-');
          const testWidthBreakWord = headerTextRef.current.getSubStringLength(startIndex, testLineBreakWord.length) + fontSize * 0.5;
          if (testWidthBreakWord <= width) {
            lines.push(testLineBreakWord);
            startIndex += testLineBreakWord.length;
            currentLine = postBreak;
          } else {
            lines.push(currentLine);
            startIndex += currentLine.length + 1;
            currentLine = word;  
          }
        } else {
          lines.push(currentLine);
          startIndex += currentLine.length + 1;
          currentLine = word;
        }
      } else {
        currentLine = testLine;
      }
    });
    lines.push(currentLine);

    setBaselineOffset(currentBaseline - bbox.y)
    setTextLines(lines);
  }, [text, fontFamily, fontSize, fontWeight, width]);

  useLayoutEffect(() => {
    if (!wrappedTextRef || !wrappedTextRef.current) {
      return;
    }
    const wbbox = wrappedTextRef.current.getBBox()
    const boxes = textLines.map((_, index) => {
      const ref = tspanRefs.current.get(index)
      return ref ? ref.getBBox() : new DOMRect(0, 0, 0, 0);
    })
    onLayout(wbbox.height, boxes);
  }, [textLines])

  const setRef = (index: number) => (el: SVGTSpanElement | null) => {
    if (el) {
      tspanRefs.current.set(index, el);
    } else {
      tspanRefs.current.delete(index);
    }
  };


  return (
    <g>
      {/* For wrapped text there's a wrapped and an unwrapped version,
          The unwrapped version allows recalculation of the wrapping
          without toggling to a one-liner before recalculation.  */}
      <text ref={headerTextRef} 
        x={x} y={headerY} 
        fontSize={fontSize} 
        fill={`rgba(255, 255, 255, 0.0)`}
        fontFamily={fontFamily}
        fontWeight={fontWeight}
        letterSpacing={letterSpacing}
        textAnchor={textAnchor}
      >
         {text}
      </text>
      {textLines.length > 0 && <text ref={wrappedTextRef}
        x={x} y={headerY} 
        fontSize={fontSize} 
        fill={color}
        fontFamily={fontFamily}
        fontWeight={fontWeight}
        letterSpacing={letterSpacing}
        textAnchor={textAnchor}
      >
        {textLines.map((line, index) => (
          <tspan key={index} x={x} dy={index ? `${fontSize * lineHeight}` : 0} ref={setRef(index)}>
            {line}
          </tspan>
        ))}
      </text>}
    </g>
  )

}